import {
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Input,
  TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { Search } from 'lucide-react'

const TEXT_FILTER_DEBOUNCE_MS = 250

interface DebouncedTextFilterProps {
  value: string
  onChange: (value: string) => void
  numeric?: boolean
  ariaLabel?: string
  placeholder?: string
}

export const DebouncedTextFilter = ({
  value,
  onChange,
  numeric = false,
  ariaLabel = 'Filter column',
  placeholder = numeric ? 'Value...' : 'Search...',
}: DebouncedTextFilterProps) => {
  const [inputValue, setInputValue] = useState(value)
  const [debouncedValue] = useDebouncedValue(inputValue, TEXT_FILTER_DEBOUNCE_MS)
  const inputRef = useRef<HTMLInputElement>(null)
  const onChangeRef = useRef(onChange)
  const inputValueRef = useRef(value)
  const isDirtyRef = useRef(false)
  const pendingValuesRef = useRef<string[]>([])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    const pendingIndex = pendingValuesRef.current.indexOf(value)
    if (pendingIndex >= 0) {
      pendingValuesRef.current.splice(0, pendingIndex + 1)
      if (inputValueRef.current === value) isDirtyRef.current = false
      return
    }

    isDirtyRef.current = false
    inputValueRef.current = value
    setInputValue(value)
  }, [value])

  useEffect(() => {
    if (!isDirtyRef.current || debouncedValue === value) return

    const pendingValues = pendingValuesRef.current
    if (pendingValues[pendingValues.length - 1] !== debouncedValue) {
      pendingValues.push(debouncedValue)
    }
    onChangeRef.current(debouncedValue)
  }, [debouncedValue, value])

  const handleInputChange = (nextValue: string) => {
    isDirtyRef.current = true
    inputValueRef.current = nextValue
    setInputValue(nextValue)
  }

  // Routed through the same path as typing, so a clear is debounced exactly like deleting the
  // text by hand. Applying it immediately would mean bypassing the pending-value bookkeeping
  // above, which is what stops a slow debounced value overwriting newer input. Focus returns
  // to the input so a keyboard user is not left on a button that has just disappeared.
  const handleClear = () => {
    handleInputChange('')
    inputRef.current?.focus()
  }

  return (
    <TextInput
      ref={inputRef}
      aria-label={ariaLabel}
      placeholder={placeholder}
      size='xs'
      type={numeric ? 'number' : 'text'}
      value={inputValue}
      onChange={(event) => handleInputChange(event.currentTarget.value)}
      leftSection={<Search size={12} />}
      rightSection={inputValue ? (
        <Input.ClearButton
          aria-label={`Clear ${ariaLabel}`}
          onClick={handleClear}
        />
      ) : null}
      rightSectionPointerEvents='auto'
    />
  )
}
